import React, { Fragment, useState } from 'react';

import { dashboardPartnerApis } from '@apis/next/partnerApis';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { PartnerFormDialog } from '@components/admin/widgets/PartnerFormDialog';
import { Button } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { cellFactory, Table } from '@components/Table/Table';
import { partnerStrings } from '@data/partners';
import { useSearch } from '@hooks/next/useSearch';
import { PartnerQueryType } from '@models/next/Partner';
import { type Partner } from '@models/next/Partner';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { DEFAULT_PAGE_SIZE } from '@utils/types';
import { useChecklist } from '@utils/useChecklist';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: `${partnerStrings.partner}管理`,
    add: '新規追加',
    details: '詳細',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '1rem',
  },
  deliveryStatusFilter: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.middleText,
    ...common.textGray500,
    '& button': {
      padding: '0 9px',
    },
    '& button[data-selected="true"]': {
      ...common.textPrimary,
      ...common.fontWeight600,
    },
    '& > button:not(:first-child)': {
      borderLeft: `1px solid ${theme.palette.grey[200]}`,
    },
    '& > button:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.grey[200]}`,
    },
  },
}));

const PartnerTypeFilter = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(PartnerQueryType);

  return (
    <div className={classes.deliveryStatusFilter}>
      <button
        data-selected={search.type === '通関業者'}
        onClick={() => updateSearch({ type: '通関業者' })}
      >
        {partnerStrings.customsPartner}
      </button>
      <button
        data-selected={search.type === '運送業者'}
        onClick={() => updateSearch({ type: '運送業者' })}
      >
        {partnerStrings.transportPartner}
      </button>
      <button
        data-selected={search.type === '倉庫業者'}
        onClick={() => updateSearch({ type: '倉庫業者' })}
      >
        {partnerStrings.warehousePartner}
      </button>
      <button
        data-selected={search.type === '税関検査業者'}
        onClick={() => updateSearch({ type: '税関検査業者' })}
      >
        {partnerStrings.customsInspectionPartner}
      </button>
      <button
        data-selected={search.type === 'その他'}
        onClick={() => updateSearch({ type: 'その他' })}
      >
        {partnerStrings.otherPartner}
      </button>
      <button data-selected={search.type == null} onClick={() => updateSearch({ type: undefined })}>
        ALL
      </button>
    </div>
  );
};

export const Partners: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [exportCsvOpen, setExportCsvOpen] = useState(false);

  const { search } = useSearch(PartnerQueryType);

  const dashboardPartnersQuery = dashboardPartnerApis.useGet('/dashboard/partner', {
    queries: { page_size: DEFAULT_PAGE_SIZE, type: search.type },
  });

  const [truckCompanyDialogOpen, setTruckCompanyDialogOpen] = useState(false);

  const { setChecked, toggleCheckAll, allCheckedAndNotEmpty, checked, checklist } = useChecklist(
    [],
    (dashboardPartnersQuery.data ?? []).map((t) => t.id),
  );

  const renderTruckCompany = (truckCompany: Partner, i: number) => {
    return (
      <Fragment key={truckCompany.id}>
        <Table.Row
          columns={[
            cellFactory.createCheckable(checked(truckCompany.id), (checked) =>
              setChecked(truckCompany.id, checked),
            ),
            cellFactory.create(<>{i + 1}</>),
            cellFactory.create(<>{truckCompany.name_ja}</>),
            cellFactory.create(
              <Link
                className={classes.link}
                to={`${url('/admin/widgets/PartnerDetails')}?id=${truckCompany.id}`}
              >
                {strings.details}
              </Link>,
            ),
            cellFactory.create(<>{truckCompany.fc_no}</>),
            cellFactory.create(truckCompany.name_en_short || '-'),
            cellFactory.create(truckCompany.type.join('/')),
          ]}
        />
      </Fragment>
    );
  };

  return (
    <Layout loading={dashboardPartnersQuery.isLoading} title={strings.title}>
      <PartnerFormDialog
        type="create"
        open={truckCompanyDialogOpen}
        onClose={() => setTruckCompanyDialogOpen(false)}
      />
      <ExportCSVDialog
        open={exportCsvOpen}
        onClose={() => setExportCsvOpen(false)}
        subject={partnerStrings.truckCompany}
        useMatch={false}
        baseUrl="/dashboard/partner/"
        selectedIds={checklist}
      />

      <div className={classes.container}>
        <SectionTitle
          actions={
            <div className={classes.buttonContainer}>
              <Button
                color="primary"
                shape="round"
                type="button"
                text={strings.add}
                onClick={() => setTruckCompanyDialogOpen(true)}
              />
              <Button
                color="primary"
                shape="round"
                type="button"
                text={formStrings.exportCsv}
                onClick={() => setExportCsvOpen(true)}
              />
            </div>
          }
        >
          {strings.title}
        </SectionTitle>

        <div style={{ height: 16 }} />

        <PartnerTypeFilter />

        <div style={{ height: 9 }} />

        <Table>
          <Table.Head>
            <Table.Row
              columns={[
                cellFactory.createCheckable(allCheckedAndNotEmpty, toggleCheckAll),
                cellFactory.create('NO.'),
                cellFactory.create(partnerStrings.name_ja),
                cellFactory.createBlank(),
                cellFactory.create(partnerStrings.fcNo),
                cellFactory.create(partnerStrings.short_name_en),
                cellFactory.create(partnerStrings.type),
              ]}
            />
          </Table.Head>
          <Table.Body>
            {dashboardPartnersQuery.data?.map((truckCompany, i) =>
              renderTruckCompany(truckCompany, i),
            )}
          </Table.Body>
        </Table>
      </div>
    </Layout>
  );
};
